import React, { useRef, useMemo, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import clsx from 'clsx';
import { withStyles, WithStyles } from '@mui/styles';
import style from './style';
import CustomSwitch from './CustomSwitch';
import './CustomVideo';

interface TextEditorProps extends WithStyles<typeof style> {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  helperText?: string;
  disableFirstChangeCondition?: boolean;
  switchType?: boolean;
  containerToolbar?: Array<string | Record<string, any>>;
  label?: string;
}

const TextEditor = ({
  classes,
  value,
  onChange,
  disabled,
  className,
  error,
  helperText,
  disableFirstChangeCondition,
  switchType,
  containerToolbar,
  label,
}: TextEditorProps) => {
  const editorRef = useRef<ReactQuill>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [firstChange, setFirstChange] = useState(true);
  const [mode, setMode] = useState(true);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: containerToolbar.length
          ? containerToolbar
          : [
              [
                'bold',
                'italic',
                'underline',
                { align: '' },
                { align: 'center' },
                { align: 'right' },
                { align: 'justify' },
                { list: 'bullet' },
                { list: 'ordered' },
                'link',
              ],
            ],
      },
      clipboard: { matchVisual: false },
    }),
    [containerToolbar],
  );

  const htmlToText = (html) => {
    let text = html;
    // text = text.replace(/\n/gi, '')
    text = text.replace(/<style([\s\S]*?)<\/style>/gi, '');
    text = text.replace(/<script([\s\S]*?)<\/script>/gi, '');
    // text = text.replace(/<a.*?href="(.*?)[?"].*?>(.*?)<\/a.*?>/gi, ' $2 $1 ');
    // text = text.replace(/<p>/gi, '');
    // text = text.replace(/<\/p>/gi, '\n');
    // text = text.replace(/<ul>/gi, '<ul>\n');
    // text = text.replace(/<\/ul>/gi, '</ul>\n');
    // text = text.replace(/<ol>/gi, '<ol>\n');
    // text = text.replace(/<\/ol>/gi, '</ol>\n');
    // text = text.replace(/<\/div>/gi, '\n\n');
    // text = text.replace(/<li>/gi, '<li>');
    // text = text.replace(/<\/li>/gi, '\n');
    // text = text.replace(/<br\s*\/?>/gi, ''); // Replace line break tags with a newline character
    // text = text.replace(/<[^>]+>/gi, '');  // Remove all other HTML tags
    // text = text.replace(/^\s*/gim, '') // Remove leading whitespace on each line
    // text = text.replace(/ ,/gi, ',')
    // text = text.replace(/ +/gi, ' ');  // Replace multiple spaces with a single space
    // text = text.replace(/\n+/gi, '\n\n');  // Replace multiple consecutive newlines with two newlines
    return text;
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      {(label || switchType) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 2,
          }}
        >
          {label && <label className={classes.formLabel}>{label}</label>}
          {switchType && (
            <div
              className={classes.switchContainer}
              style={{ position: 'static' }}
            >
              <CustomSwitch
                mode={mode}
                onChange={() => {
                  setMode(!mode);
                }}
              />
            </div>
          )}
        </div>
      )}
      {mode ? (
        <ReactQuill
          ref={editorRef}
          className={clsx(
            classes.quillContainer,
            disabled ? classes.disabled : '',
            {
              [className]: !!className,
            },
            { [classes.error]: error },
          )}
          theme='snow'
          value={value || ''}
          onChange={(v, delta, source) => {
            const { ops } = delta;
            const isUploadImg = ops.some((op) => !!op?.insert?.image);

            // Normalize empty Quill value (<p><br></p>) to empty string
            const normalizedValue = v === '<p><br></p>' ? '' : v;

            if (disableFirstChangeCondition) {
              onChange(normalizedValue);
            } else {
              setFirstChange(false);
              /*
                only update onChange when source is "user"
                upload image source is always "api"
                because React Quill uses a built-in mechanism to store uploaded images on a server through an API
              */
              if (
                (!firstChange && (source === 'user' || isUploadImg)) ||
                (firstChange && source === 'user')
              ) {
                onChange(normalizedValue);
              }
            }
          }}
          modules={modules}
          formats={['bold', 'italic', 'underline', 'align', 'list', 'link']}
          bounds='.container'
          preserveWhitespace
          readOnly={disabled}
        />
      ) : (
        <textarea
          style={{ overflow: 'auto', maxHeight: 160 }}
          className={classes.textArea}
          value={htmlToText(value)}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange?.(e.target.value);
          }}
        />
      )}
      {helperText && (
        <div style={{ color: '#F24822', fontSize: 12, marginTop: 4 }}>
          {helperText}
        </div>
      )}
    </div>
  );
};

TextEditor.defaultProps = {
  value: '',
  className: '',
  onChange: () => {},
  containerToolbar: [],
  disabled: false,
  error: false,
  disableFirstChangeCondition: false,
  switchType: true,
};

export default withStyles(style)(TextEditor);
